import { useCallback, useEffect, useState } from 'react';
import type { Agent, Integration, Trigger, TriggerStatus, TriggerType } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/Tabs';

const TRIGGER_TYPES: TriggerType[] = ['webhook', 'email', 'teams', 'schedule', 'meeting'];

const TYPE_LABEL: Record<TriggerType, string> = {
  webhook: 'Webhook',
  email: 'Email',
  teams: 'Teams',
  schedule: 'Schedule',
  meeting: 'Meeting',
};

interface IntakeTestResult {
  runId: string;
  label?: string;
  matched: boolean;
}

function errorMessage(err: unknown): string {
  if (!(err instanceof Error)) return 'Request failed';
  try {
    const parsed = JSON.parse(err.message) as { message?: string | string[] };
    if (typeof parsed.message === 'string') return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

function triggerBadge(status: TriggerStatus): 'success' | 'secondary' | 'warning' {
  switch (status) {
    case 'active':
      return 'success';
    case 'disabled':
      return 'secondary';
    case 'configured':
      return 'warning';
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}

function hasSigningSecret(trigger: Trigger): boolean {
  const config = (trigger.config ?? {}) as Record<string, unknown>;
  return typeof config['secretRef'] === 'string' && config['secretRef'].length > 0;
}

function publishIntegrationName(trigger: Trigger, integrations: Integration[]): string | undefined {
  const config = (trigger.config ?? {}) as Record<string, unknown>;
  const id = config['publishIntegrationId'];
  if (typeof id !== 'string' || !id) return undefined;
  return integrations.find((i) => i.id === id)?.name ?? id.slice(0, 8);
}

export function TriggersPage() {
  const { workspaces, workspaceId } = useApp();
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [type, setType] = useState<TriggerType>('webhook');
  const [name, setName] = useState('');
  const [targetAgentId, setTargetAgentId] = useState('');
  const [publishIntegrationId, setPublishIntegrationId] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<TriggerType>('webhook');
  const [editTargetAgentId, setEditTargetAgentId] = useState('');
  const [secretEditing, setSecretEditing] = useState<string | null>(null);
  const [triggerSecret, setTriggerSecret] = useState('');
  const [testText, setTestText] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<Record<string, IntakeTestResult>>({});
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState<string | null>(null);
  const [invoiceError, setInvoiceError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const rows = await apiClient.listTriggers();
    setTriggers(rows);
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) {
      setTriggers([]);
      return;
    }
    void load().catch((err: unknown) => setError(errorMessage(err)));
    void apiClient.listAgents().then((r) => setAgents(r.items)).catch(() => setAgents([]));
    void apiClient.listIntegrations().then(setIntegrations).catch(() => setIntegrations([]));
  }, [workspaceId, load]);

  function nameOf(id?: string | null): string {
    if (!id) return 'unset';
    return agents.find((a) => a.id === id)?.name ?? id.slice(0, 8);
  }

  async function create(): Promise<void> {
    const secret = signingSecret.trim();
    const pubIntId = publishIntegrationId.trim();
    const config: Record<string, unknown> = {};
    if (pubIntId) config['publishIntegrationId'] = pubIntId;
    setError(null);
    try {
      const created = await apiClient.createTrigger({
        type,
        name: name.trim(),
        config,
        targetAgentId: targetAgentId || undefined,
      });
      if (secret) {
        await apiClient.updateTriggerSecrets(created.id, { signingSecret: secret });
      }
      setName('');
      setSigningSecret('');
      setTargetAgentId('');
      setPublishIntegrationId('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function setStatus(id: string, status: TriggerStatus): Promise<void> {
    setError(null);
    try {
      await apiClient.setTriggerStatus(id, status);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function remove(id: string): Promise<void> {
    setError(null);
    try {
      await apiClient.deleteTrigger(id);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function sendTest(id: string): Promise<void> {
    const text = testText[id];
    if (!text) return;
    setError(null);
    try {
      const result = await apiClient.sendIntakeTest(id, text);
      setTestResult((prev) => ({
        ...prev,
        [id]: { runId: result.runId, label: result.label, matched: result.matched },
      }));
      setTestText((prev) => ({ ...prev, [id]: '' }));
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function receiveTestInvoice(): Promise<void> {
    setInvoiceBusy(true);
    setInvoiceResult(null);
    setInvoiceError(false);
    try {
      const res = await apiClient.receiveApInvoice('SC-01');
      setInvoiceResult(`Received ${res.case.case_id} (${res.case.status})`);
    } catch (err) {
      setInvoiceError(true);
      setInvoiceResult(errorMessage(err));
    } finally {
      setInvoiceBusy(false);
    }
  }

  function startEdit(trigger: Trigger): void {
    setEditingId(trigger.id);
    setEditName(trigger.name);
    setEditType(trigger.type);
    setEditTargetAgentId(trigger.targetAgentId ?? '');
  }

  async function saveEdit(id: string): Promise<void> {
    setError(null);
    try {
      await apiClient.updateTrigger(id, {
        name: editName.trim(),
        type: editType,
        targetAgentId: editTargetAgentId || null,
      });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function saveTriggerSecret(id: string): Promise<void> {
    const secret = triggerSecret.trim();
    if (!secret) return;
    setError(null);
    try {
      await apiClient.updateTriggerSecrets(id, { signingSecret: secret });
      setSecretEditing(null);
      setTriggerSecret('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <div>
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Triggers</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Multi-channel intake points that start governed runs for{' '}
          <strong>{workspace?.name || 'no workspace'}</strong>.
        </p>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p> : null}

      {!workspaceId ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm dark:border-gray-800 dark:bg-gray-900">
          Select a workspace from the sidebar to manage its triggers.
        </div>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-3 lg:col-span-2">
            {triggers.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm dark:border-gray-800 dark:bg-gray-900">
                No triggers yet. Add a webhook to start receiving intake.
              </div>
            ) : null}
            {triggers.map((trigger) => {
              const published = publishIntegrationName(trigger, integrations);
              const result = testResult[trigger.id];
              return (
                <Card key={trigger.id} className="p-4">
                  {editingId === trigger.id ? (
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="w-16">Name</span>
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="w-16 text-xs text-gray-500">Type</span>
                        <Tabs value={editType} onValueChange={(value) => setEditType(value as TriggerType)}>
                          <TabsList className="flex h-auto flex-wrap">
                            {TRIGGER_TYPES.map((item) => (
                              <TabsTrigger key={item} value={item}>{TYPE_LABEL[item]}</TabsTrigger>
                            ))}
                          </TabsList>
                        </Tabs>
                      </div>
                      <label className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="w-16">Entrypoint</span>
                        <select
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          value={editTargetAgentId}
                          onChange={(e) => setEditTargetAgentId(e.target.value)}
                        >
                          <option value="">Default</option>
                          {agents.map((agent) => (
                            <option key={agent.id} value={agent.id}>{agent.name}</option>
                          ))}
                        </select>
                      </label>
                      <div className="mt-1 flex gap-2">
                        <Button size="sm" onClick={() => void saveEdit(trigger.id)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <strong className="text-gray-900 dark:text-white">{trigger.name}</strong>
                          <div className="text-xs text-gray-500">{trigger.type}</div>
                        </div>
                        <Badge variant={triggerBadge(trigger.status)}>{trigger.status}</Badge>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className="text-gray-500">Endpoint</span>
                        <code className="rounded bg-gray-100 px-2 py-1 dark:bg-gray-800">{`${location.origin}/api/intake/${trigger.id}`}</code>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void navigator.clipboard?.writeText(`${location.origin}/api/intake/${trigger.id}`)}
                        >
                          Copy
                        </Button>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        entrypoint: <strong>{nameOf(trigger.targetAgentId)}</strong>
                      </div>
                      <div className="text-xs text-gray-500">
                        signing secret: <strong>{hasSigningSecret(trigger) ? 'configured' : 'not configured'}</strong>
                      </div>
                      {published ? (
                        <div className="text-xs text-gray-500">
                          publish to: <strong>{published}</strong>
                        </div>
                      ) : null}
                      {trigger.type === 'webhook' || trigger.type === 'email' ? (
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="Send a test message"
                              value={testText[trigger.id] ?? ''}
                              onChange={(e) => setTestText((prev) => ({ ...prev, [trigger.id]: e.target.value }))}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid="trigger-test"
                              disabled={!testText[trigger.id]}
                              onClick={() => void sendTest(trigger.id)}
                            >
                              Test
                            </Button>
                          </div>
                          {result ? (
                            <div className="mt-1 text-xs text-green-700 dark:text-green-300">
                              started run {result.runId.slice(0, 8)} → {result.matched ? `classified as ${result.label}` : 'default route'}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {trigger.type === 'email' ? (
                        <div className="mt-2 flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            type="button"
                            data-testid="trigger-receive-invoice"
                            disabled={invoiceBusy}
                            onClick={() => void receiveTestInvoice()}
                          >
                            Receive test invoice
                          </Button>
                          {invoiceResult ? (
                            <span className={`text-xs ${invoiceError ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-300'}`}>
                              {invoiceResult}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="mt-2 flex gap-2">
                        {trigger.status !== 'active' ? (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="trigger-activate"
                            onClick={() => void setStatus(trigger.id, 'active')}
                          >
                            Activate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600"
                            onClick={() => void setStatus(trigger.id, 'disabled')}
                          >
                            Disable
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => startEdit(trigger)}>Edit</Button>
                        <Button size="sm" variant="ghost" aria-label="Delete trigger" onClick={() => void remove(trigger.id)}>
                          Delete
                        </Button>
                      </div>
                    </>
                  )}
                  {secretEditing === trigger.id ? (
                    <div className="mt-2 flex gap-2">
                      <Input
                        type="password"
                        autoComplete="new-password"
                        placeholder="New signing secret"
                        value={triggerSecret}
                        onChange={(e) => setTriggerSecret(e.target.value)}
                      />
                      <Button size="sm" disabled={!triggerSecret.trim()} onClick={() => void saveTriggerSecret(trigger.id)}>
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSecretEditing(null);
                          setTriggerSecret('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-2"
                      onClick={() => {
                        setSecretEditing(trigger.id);
                        setTriggerSecret('');
                      }}
                    >
                      {hasSigningSecret(trigger) ? 'Rotate secret' : 'Add signing secret'}
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>

          <Card className="h-fit p-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">New trigger</h2>
            <div className="mt-3">
              <span className="mb-1.5 block text-xs text-gray-500">Channel</span>
              <Tabs value={type} onValueChange={(value) => setType(value as TriggerType)}>
                <TabsList className="flex h-auto flex-wrap">
                  {TRIGGER_TYPES.map((item) => (
                    <TabsTrigger key={item} value={item}>{TYPE_LABEL[item]}</TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <Input className="mt-3" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <label className="mt-3 block">
              <span className="text-xs text-gray-500">Signing secret (optional)</span>
              <Input
                className="mt-1"
                type="password"
                autoComplete="new-password"
                placeholder="Webhook HMAC secret"
                value={signingSecret}
                onChange={(e) => setSigningSecret(e.target.value)}
              />
              <span className="mt-1 block text-xs text-gray-500">
                Stored in Vault. Leave blank for dev-only unsigned intake.
              </span>
            </label>
            <label className="mt-3 block">
              <span className="mb-1.5 block text-xs text-gray-500">Entrypoint agent</span>
              <select
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                value={targetAgentId}
                onChange={(e) => setTargetAgentId(e.target.value)}
              >
                <option value="">Default</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>{agent.name}</option>
                ))}
              </select>
            </label>
            <label className="mt-3 block">
              <span className="mb-1.5 block text-xs text-gray-500">Publish integration (optional)</span>
              <select
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                value={publishIntegrationId}
                onChange={(e) => setPublishIntegrationId(e.target.value)}
              >
                <option value="">None</option>
                {integrations.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </label>
            <Button className="mt-3" size="sm" disabled={!name.trim()} onClick={() => void create()}>
              Add trigger
            </Button>
            <p className="mt-3 text-xs text-gray-400">
              Webhooks accept signed POSTs and classify input via taxonomy routes before starting a run.
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}
